import { AfterViewInit, Component, ElementRef, Inject, OnInit, Optional, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { ApiService } from 'src/app/services/api.service';
import { environment } from 'src/environments/environment';
import { BasicPopupComponent } from '../basic-popup/basic-popup.component';

@Component({
  selector: 'app-capture-image-from-camera',
  templateUrl: './capture-image-from-camera.component.html',
  styleUrls: ['./capture-image-from-camera.component.css'],
  standalone: false
})
export class CaptureImageFromCameraComponent implements AfterViewInit {
  @ViewChild('video', { static: true }) video!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvas', { static: true }) canvas!: ElementRef<HTMLCanvasElement>;

  captures: string[] = [];
  isFrontCamera = false;
  stream: MediaStream | null = null;
  capturedImage: string;
  pageType: any;
  pageTitle: any;
  isTakePhoto = false;
  latitude: string;
  longitude: string;
  timestamp: string;

  videoTrack!: MediaStreamTrack;

  zoomLevel = 1;
  minZoom = 1;
  maxZoom = 4;

  private initialPinchDistance = 0;
  private initialZoom = 1;
  isLandscape = false;

  constructor(
    public _api: ApiService,
    private spinner: NgxSpinnerService,
    private dialog: MatDialog,
    @Optional() public dialogRef: MatDialogRef<any>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  checkOrientation() {
    this.isLandscape = window.matchMedia('(orientation: landscape)').matches;
  }

  ngOnInit(): void {
    this.checkOrientation();
    window.addEventListener('resize', () => this.checkOrientation());

    const timestamp = new Date().toISOString();
    this.timestamp = timestamp;
    if (navigator.geolocation) {
      this.spinner.show();
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const geoLocation = {
            latitude: position.coords.latitude.toFixed(6),
            longitude: position.coords.longitude.toFixed(6)
          };
          this.latitude = geoLocation.latitude;
          this.longitude = geoLocation.longitude;
          this.spinner.hide();
          this.startCamera();
        },
        (error) => {
          this.spinner.hide();
          this.closeModal();
          switch (error.code) {
            case 1: // PERMISSION_DENIED
              this.showPopup(
                'Location Access Needed',
                'To use the app, please turn on location access in your browser settings.',
                'PERMISSION_DENIED',
                environment.help_doc,
              );
              break;
            case 2: // POSITION_UNAVAILABLE
              this.showPopup(
                'Location Unavailable',
                'We could not retrieve your location. To use the app, please turn on location access in your browser settings.',
                'POSITION_UNAVAILABLE',
                environment.help_doc,
              );
              break;
            case 3: // TIMEOUT
              this.showPopup(
                'Request Timed Out',
                'The request to retrieve your location timed out. Please try again.',
                'TIMEOUT',
                environment.help_doc,
              );
              break;
            default:
              this.showPopup(
                'Error',
                'An unknown error occurred while accessing your location.',
                'Error',
                environment.help_doc,
              );
          }
          this.stopCamera();
          console.log('-------------------location is not enable', error);
        }
      );
    } else {

    }
  }

  showPopup(title: string, message: string, case_type: string, help_doc: string) {
    const dialogRef = this.dialog.open(BasicPopupComponent, {
      data: {
        pageTitle: title,
        message: message,
        pageType: case_type,
        help: help_doc
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
      }
    });
  }

  ngAfterViewInit() {
    if (this.latitude && this.longitude) {
      this.startCamera();
    }
  }

  // site visite camera
  async startCamera() {
    if (!this.video?.nativeElement) {
      console.error('Video element is not available.');
      return;
    }
    this.isTakePhoto = false;
    this.capturedImage = null;

    const facingMode = this.isFrontCamera ? 'user' : 'environment';

    const constraints = {
      video: { facingMode, width: 1280, height: 960 },
      audio: false
    };

    if (this.stream) { this.stopCamera(); }

    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      this.stream = stream;
      this.video.nativeElement.srcObject = stream;

      this.videoTrack = stream.getVideoTracks()[0];

      // ✅ Check hardware zoom support
      const capabilities: any = this.videoTrack.getCapabilities();

      if (capabilities.zoom) {
        this.minZoom = capabilities.zoom.min;
        this.maxZoom = capabilities.zoom.max;
        this.zoomLevel = capabilities.zoom.min;
      }

    } catch (error) {
      console.error('Camera error', error);
      alert('Camera access is required');
    }
  }

  async onZoomSliderChange() {
    if (!this.videoTrack) { return; }

    const capabilities: any = this.videoTrack.getCapabilities();

    if (capabilities.zoom) {
      await this.videoTrack.applyConstraints({
        advanced: [{ zoom: this.zoomLevel } as any]
      });
    } else {
      // fallback CSS zoom
      this.video.nativeElement.style.transform =
        `scale(${this.zoomLevel})`;
    }
  }

  onTouchStart(event: TouchEvent) {
    if (event.touches.length === 2) {
      this.initialPinchDistance = this.getDistance(event.touches);
      this.initialZoom = this.zoomLevel;
    }
  }

  onTouchMove(event: TouchEvent) {
    if (event.touches.length === 2) {
      const currentDistance = this.getDistance(event.touches);
      const scale = currentDistance / this.initialPinchDistance;

      this.zoomLevel = Math.min(
        this.maxZoom,
        Math.max(this.minZoom, this.initialZoom * scale)
      );

      this.onZoomSliderChange();
    }
  }

  getDistance(touches: TouchList) {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }


  stopCamera() {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
      this.isTakePhoto = false;
    }
  }

  toggleCamera() {
    this.isFrontCamera = !this.isFrontCamera;
    this.startCamera();
  }

  capture(): void {
    const canvas = this.canvas.nativeElement;
    const video = this.video.nativeElement;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext('2d');
    if (context) {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
    }

    this.capturedImage = canvas.toDataURL('image/png');
    this.captures.push(this.capturedImage);

    // Stop the camera
    this.stopCamera();

    // Replace video feed with the captured image
    video.srcObject = null; // Clear the video stream
    video.poster = this.capturedImage; // Use the captured image as the placeholder
    this.isTakePhoto = true;
  }

  get toggleButtonText(): string {
    return this.isFrontCamera ? 'Switch to Back Camera' : 'Switch to Front Camera';
  }

  sendImage() {
    this.stopCamera();
    const result = {
      latitude: this.latitude,
      longitude: this.longitude,
      timestamp:  this.timestamp,
      image: this.capturedImage,
      section_type: this.data['message']
    };
    this.dialogRef.close(result);
    if (navigator.geolocation) {
      this.spinner.show();
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const geoLocation = {
            latitude: position.coords.latitude.toFixed(6),
            longitude: position.coords.longitude.toFixed(6)
          };
          this.latitude = geoLocation.latitude;
          this.longitude = geoLocation.longitude;
          this.spinner.hide();
        },
        (error) => {

        }
      );
    } else {

    }
  }

  closeModal() {
    this.stopCamera();
    this.dialogRef.close();
  }

}
